import React from 'react';
import '../caria.css';
import { StatusChip } from '../../../commodity/components/ui';
import { HeroGallery } from '../components/HeroGallery';
import { PriceBlock } from '../components/PriceBlock';
import { SpecsTable } from '../components/SpecsTable';
import { TrustSection } from '../components/TrustSection';
import { DisclosurePanel } from '../components/DisclosurePanel';
import { ContactCTA, ContactState } from '../components/ContactCTA';
import { VehicleDetailVM } from '../types';

/**
 * VehicleDetail (Epic 4, S4.2) — buyer detail screen skeleton.
 *
 * Detail hierarchy locked (backlog S4.2 / requirement [F]): hero →
 * price → title → specs → trust → disclosure → contact. Progressive
 * disclosure lives inside SpecsTable / DisclosurePanel.
 *
 * Screen states (S4.4): loading · active · paused · pricing-unavailable
 * · contact-created (success) · contact-failed — all prop-driven so the
 * screen is demoable before Epic 7 wires data.
 */
export interface VehicleDetailProps {
  vehicle?: VehicleDetailVM;
  loading?: boolean;
  contactState?: ContactState;
  onContact?: () => void;
}

export const VehicleDetail: React.FC<VehicleDetailProps> = ({
  vehicle,
  loading = false,
  contactState = 'idle',
  onContact,
}) => {
  if (loading || !vehicle) {
    return (
      <main className="caria-detail" data-testid="vehicle-detail-loading" aria-busy="true">
        <div className="caria-gallery__main">Cargando…</div>
        <div className="caria-priceblock">
          <div className="caria-card__skeleton" style={{ width: '60%', height: '2rem' }} />
        </div>
      </main>
    );
  }

  return (
    <main className="caria-detail" data-testid="vehicle-detail">
      <div>
        <HeroGallery photos={vehicle.photos} />
        <h1 style={{ marginTop: '1rem' }}>
          {vehicle.title} <StatusChip status={vehicle.status} />
        </h1>
        <SpecsTable specs={vehicle.specs} />
        <TrustSection trust={vehicle.trust} />
        <DisclosurePanel text={vehicle.disclosureText} />
      </div>
      <aside className="caria-detail__sticky">
        <PriceBlock
          sellerPrice={vehicle.sellerPrice}
          suggestedPrice={vehicle.suggestedPrice}
          pricingConfidence={vehicle.pricingConfidence}
          decision={vehicle.decision}
        />
        <div style={{ marginTop: '1rem' }}>
          <ContactCTA state={contactState} onContact={onContact} />
        </div>
      </aside>
    </main>
  );
};

export default VehicleDetail;
